// 프로젝트 작업 화면: ① 기본 정보 ② 장소 ③ 무대 디자인 ④ 요소 합의 ⑤ 장면 ⑥ 연출컷
import { byId, CAPACITIES, capacityKo, EFFECTS, ELEMENT_KINDS, EVENT_KINDS, PEOPLE, PURPOSES, SCENES, STAGE_PRESETS, TIMES, TONES, VENUE_TYPES, type Option } from "../../shared/knowledge";
import { sceneHash } from "../../shared/prompt";
import type { Element, Project } from "../../shared/types";
import { icon } from "../icons";
import { actionBar, api, busy, CAMERAS_UI, cardHead, chipGroup, clear, confirmBox, debounce, devDetails, emptyState, foldSm, h, helpTip, mediaUrl, menu, pickFiles, plainKo, redraw, sceneLine, segmented, shrinkImage, toast, venueGroups, type BarOpts, type Child } from "../lib";
import { go, state } from "../store";
import { assetsPanel, MAX_USED } from "./assets";
import { renderCuts } from "./cuts";
import { openSpaceBuilder } from "./space-builder";
import { renderStage } from "./stage";

export type Ctx = { p: Project; set(p: Project): void; step(name: string): void; bar(o: BarOpts | null): void };

const STEPS = [
  { id: "info", ko: "기본 정보" },
  { id: "space", ko: "장소" },
  { id: "stage", ko: "무대 디자인" },
  { id: "elements", ko: "요소 합의" },
  { id: "scene", ko: "장면" },
  { id: "cuts", ko: "연출컷" },
] as const;

const PHASES = [
  { id: "prep", ko: "준비", steps: ["info", "space"] },
  { id: "p1", ko: "1차 · 무대 시안", steps: ["stage", "elements"] },
  { id: "p2", ko: "2차 · 연출컷", steps: ["scene", "cuts"] },
] as const;

const LABELS = ["관중석 위 전경", "정면", "측면", "위에서 본 평면", "객석 눈높이", "무대 가까이"];

/** '무대 자리: 한쪽 끝' → '한쪽 끝' */
const stageKo = (id: string | undefined) => (STAGE_PRESETS.find((s) => s.id === id)?.ko || "").replace(/^무대 자리:\s*/, "");
const errMsg = (err: unknown) => (err instanceof Error ? err.message : String(err));
const nextLabel = (label: string) => [h("span", null, label), icon("chevron-right", 16)];

function stepStatus(p: Project, id: string): { text: string; done: boolean } {
  const d = p.data;
  const pending = d.elements.filter((e) => e.status === "proposed").length;
  switch (id) {
    case "info":
      // 행사 이름만 적은 것은 아직 덜 끝났다 (장소나 규모가 있어야 3D 모형·장면 글에 쓰인다)
      if (!d.brief.eventName) return { text: "시작 전", done: false };
      return d.brief.venueType || d.brief.capacity ? { text: "완료", done: true } : { text: "장소·규모 남음", done: false };
    case "space": {
      const n = d.space.filter((s) => s.source !== "stage" && s.source !== "stage-angle").length;
      return { text: n ? `그림 ${n}장` : "시작 전", done: n > 0 };
    }
    case "stage":
      return d.stage?.approvedKey ? { text: "확정", done: true } : d.stage?.skipped ? { text: "건너뜀", done: true } : { text: d.stage?.directions?.length ? "고르는 중" : "시작 전", done: false };
    case "elements":
      if (pending) return { text: `확인 ${pending}개 남음`, done: false };
      return d.elements.length ? { text: `${d.elements.filter((e) => e.status === "confirmed").length}개 확정`, done: true } : { text: "시작 전", done: false };
    case "scene":
      return { text: plainKo(byId(SCENES, d.scene.scene)?.ko) || "시작 전", done: Boolean(d.scene.scene) };
    default:
      return { text: d.canonCutId ? "승인본 있음" : "", done: Boolean(d.canonCutId) };
  }
}

function defaultStep(p: Project): string {
  const d = p.data;
  if (!d.space.length && !d.elements.length) return "space";
  if (!d.stage?.approvedKey && !d.stage?.skipped) return "stage";
  if (d.elements.some((e) => e.status === "proposed")) return "elements";
  if (!d.scene.scene) return "scene";
  return "cuts";
}

export async function renderProject(main: HTMLElement, id: string, step: string, alive: () => boolean = () => true) {
  let p = await api<Project>(`/api/projects/${id}`);
  // 불러오는 사이 다른 화면으로 옮겼으면 아무것도 하지 않는다 (색·막대를 덮어쓰지 않게)
  if (!alive()) return;
  clear(main);
  const current = STEPS.some((s) => s.id === step) ? step : defaultStep(p);
  const idx = STEPS.findIndex((s) => s.id === current);
  const phase = PHASES.find((g) => (g.steps as readonly string[]).includes(current))!.id;
  document.body.dataset.phase = phase;
  main.classList.add("has-bar");

  const title = h("h1", null, p.title);
  const summary = h("p", { class: "proj-sum" });
  const stepper = h("nav", { class: "stepper", "aria-label": "단계" });
  const body = h("section", { "aria-label": STEPS[idx].ko });
  const barSlot = h("div", { class: "action-bar-slot" });
  const prevStep = idx > 0 ? STEPS[idx - 1] : null;
  const defaultBack = prevStep ? { label: prevStep.ko, onclick: () => ctx.step(prevStep.id) } : { label: "내 프로젝트", onclick: () => go("#/") };

  const ctx: Ctx = {
    get p() {
      return p;
    },
    set(np: Project) {
      p = np;
      title.textContent = p.title;
      drawSummary();
      drawStepper();
    },
    step(name: string) {
      go(`#/p/${p.id}/${name}`);
    },
    bar(o: BarOpts | null) {
      clear(barSlot);
      if (o) barSlot.append(actionBar({ back: defaultBack, ...o }));
    },
  };

  function drawSummary() {
    const b = p.data.brief;
    summary.textContent = [byId(EVENT_KINDS, b.eventKind)?.ko, b.venueMode === "real" && b.venueName ? b.venueName : byId(VENUE_TYPES, b.venueType)?.ko, b.capacity ? capacityKo(b.capacity) : ""].filter(Boolean).join(" · ");
    summary.hidden = !summary.textContent;
  }

  function drawStepper() {
    clear(stepper);
    let n = 0;
    for (const g of PHASES) {
      const box = h("div", { class: `step-group ${g.id}`, "data-label": g.ko, role: "group", "aria-label": g.ko });
      for (const sid of g.steps) {
        const s = STEPS.find((x) => x.id === sid)!;
        const st = stepStatus(p, sid);
        n++;
        const on = sid === current;
        box.append(
          h(
            "a",
            { class: `step${on ? " on" : ""}${st.done ? " done" : ""}`, href: `#/p/${p.id}/${sid}`, "data-phase": g.id, "aria-current": on ? "step" : null },
            h("span", { class: "num" }, st.done && !on ? icon("check", 14, "완료") : String(n)),
            h("span", { class: "txt" }, h("span", { class: "label" }, s.ko), st.text ? h("span", { class: "sub" }, st.text) : null),
          ),
        );
      }
      stepper.append(box);
    }
    requestAnimationFrame(() => {
      const on = stepper.querySelector<HTMLElement>(".step.on");
      if (on && stepper.scrollWidth > stepper.clientWidth) stepper.scrollLeft = on.offsetLeft - (stepper.clientWidth - on.offsetWidth) / 2;
    });
  }

  const projMenu = menu(
    [
      {
        label: "프로젝트 지우기",
        icon: "trash",
        danger: true,
        onclick: async () => {
          if (!(await confirmBox("프로젝트 지우기", `"${p.title}" 프로젝트를 지울까요? 지운 프로젝트는 목록에서 사라져요.`, "지우기", { danger: true }))) return;
          try {
            await api(`/api/projects/${p.id}`, { method: "DELETE" });
            go("#/");
          } catch (err) {
            toast(errMsg(err), "err");
          }
        },
      },
    ],
    "프로젝트 메뉴",
  );

  drawSummary();
  drawStepper();
  main.append(
    h(
      "div",
      { class: "proj-head" },
      h("a", { class: "back-link", href: "#/" }, icon("chevron-left", 16), "내 프로젝트"),
      h("div", { class: "proj-title" }, h("div", { class: "proj-title-text" }, title, summary), projMenu),
    ),
    stepper,
    body,
  );
  main.after(barSlot);

  if (current === "info") renderInfo(body, ctx);
  else if (current === "space") renderSpace(body, ctx);
  else if (current === "stage") await renderStage(body, ctx);
  else if (current === "elements") renderElements(body, ctx);
  else if (current === "scene") renderScene(body, ctx);
  else await renderCuts(body, ctx);
}

/** 고르기: 하나만 고르고 4개 이하면 나눔 단추, 아니면 칩. 고른 것에 hint 가 있으면 아래 한 줄로 보여 준다 */
function chips(list: Option[], selected: string[], multi: boolean, onChange: (v: string[]) => void, hidden: string[] = [], label = "고르기"): HTMLElement {
  const visible = list.filter((x) => !hidden.includes(x.id));
  if (!multi && visible.length <= 4) {
    return segmented(
      visible.map((o) => ({ id: o.id, ko: o.ko, hint: o.hint })),
      selected[0] ?? null,
      (id) => id && onChange([id]),
      label,
      { showHint: visible.some((o) => o.hint) },
    );
  }
  return chipGroup(visible, selected, multi, onChange, label);
}

// ───────── ① 기본 정보 ─────────

function renderInfo(body: HTMLElement, ctx: Ctx) {
  const saveState = h("span", null, "고르는 대로 저장돼요");
  let saving = 0;
  const summaryCard = h("div", { class: "card summary-card" });
  const send = async (patch: Record<string, unknown>) => {
    saving++;
    saveState.textContent = "저장 중…";
    try {
      ctx.set(await api<Project>(`/api/projects/${ctx.p.id}/brief`, { method: "PATCH", body: patch }));
      if (--saving === 0) saveState.textContent = "저장됨";
    } catch (err) {
      saving--;
      saveState.textContent = "저장하지 못했어요";
      toast(errMsg(err), "err");
    }
    drawSummary();
  };
  // 칸마다 따로 미뤄 저장한다 (한 칸을 쓰다 다른 칸으로 넘어가도 앞의 것이 빠지지 않게)
  const savers = new Map<string, (patch: Record<string, unknown>) => void>();
  const saveLater = (key: string, patch: Record<string, unknown>) => {
    if (!savers.has(key)) savers.set(key, debounce((pt: Record<string, unknown>) => void send(pt), 500));
    saveState.textContent = "저장 중…";
    savers.get(key)!(patch);
  };
  const opt = h("span", { class: "opt" }, "(선택)");
  const text = (label: Child, key: string, value: string, placeholder = "", help?: string, multiline = false) => {
    const el = multiline ? (h("textarea", { rows: 3, maxlength: 2000, placeholder }, value) as HTMLTextAreaElement) : h("input", { value, maxlength: 120, placeholder });
    el.addEventListener("input", () => saveLater(key, { [key]: el.value, ...(key === "eventName" ? { title: el.value } : {}) }));
    return h("label", null, h("span", null, label), el, help ? h("span", { class: "help" }, help) : null);
  };

  const b0 = ctx.p.data.brief;
  const sports = () => ctx.p.data.brief.eventKind === "sports";
  const typeField = text("행사 종류 자세히", "eventType", b0.eventType);
  const venueNameField = text("장소 이름", "venueName", b0.venueName || "", "", "적으면 AI가 장면을 그릴 때 참고해요");
  // 예시 글은 행사 종류에 맞춘다 (종류를 바꾸면 다시 맞춘다)
  const syncExamples = () => {
    typeField.querySelector("input")!.placeholder = sports() ? "예: 국제 체육대회 개회식" : "예: 음악 페스티벌 메인 무대";
    venueNameField.querySelector("input")!.placeholder = sports() ? "예: 청주종합경기장 주경기장" : "예: 올림픽공원 88잔디마당";
  };
  syncExamples();
  venueNameField.hidden = (b0.venueMode || "virtual") !== "real";
  const venueBox = h("div", { class: "stack" });
  const capBox = h("div");

  let sportsOpen: boolean | null = null; // 사용자가 열거나 접은 '경기장' 묶음은 그대로 둔다
  const venueCard = (v: (typeof VENUE_TYPES)[number]) => {
    const on = ctx.p.data.brief.venueType === v.id;
    return h(
      "button",
      {
        type: "button",
        class: `option-card${on ? " on" : ""}`,
        "aria-pressed": on ? "true" : "false",
        "data-k": `venue-${v.id}`,
        onclick: () => void send({ venueType: v.id, ...(ctx.p.data.brief.capacity ? {} : { capacity: v.capacity }) }).then(() => (drawVenue(), drawCap())),
      },
      h("span", { class: "t" }, v.ko),
      v.hint ? h("span", { class: "d" }, v.hint) : null,
    );
  };
  const drawVenue = () =>
    redraw(venueBox, () => {
      clear(venueBox);
      const { groups, other } = venueGroups(sports());
      for (const g of groups) {
        const grid = h("div", { class: "option-grid" }, g.list.map(venueCard));
        if (g.id === "sports" && !sports()) {
          // 운동 행사가 아니면 '경기장'은 접어 둔다 (고른 것이 그 안에 있으면 펼친다)
          const det = h(
            "details",
            { class: "venue-group venue-more", open: sportsOpen ?? g.list.some((v) => v.id === ctx.p.data.brief.venueType) },
            h("summary", { class: "group-title" }, g.ko, h("span", { class: "num" }, String(g.list.length)), icon("chevron-down", 16)),
            grid,
          );
          det.addEventListener("toggle", () => (sportsOpen = det.open));
          venueBox.append(det);
        } else venueBox.append(h("div", { class: "venue-group" }, h("h4", { class: "group-title" }, g.ko), grid));
      }
      venueBox.append(h("div", { class: "venue-group" }, h("div", { class: "option-grid" }, other.map(venueCard))));
    });

  const drawCap = () =>
    redraw(capBox, () => {
    clear(capBox);
    const cap = ctx.p.data.brief.capacity;
    const capInput = h("input", { type: "number", min: 50, max: 200000, step: 100, value: cap || "", placeholder: "직접 적기", inputmode: "numeric", "aria-label": "관객 수 직접 적기", "data-k": "cap-input" });
    capInput.addEventListener("change", () => void send({ capacity: Number(capInput.value) }).then(drawCap));
    capBox.append(
      h(
        "div",
        { class: "row wrap cap-row" },
        chips(
          CAPACITIES.map((n) => ({ id: String(n), ko: capacityKo(n), en: "" })),
          cap && CAPACITIES.includes(cap) ? [String(cap)] : [],
          false,
          (v) => void send({ capacity: Number(v[0]) }).then(drawCap),
          [],
          "관객 규모",
        ),
        h("div", { class: "input-suffix cap-input", "data-suffix": "명" }, capInput),
      ),
    );
    });

  function drawSummary() {
    const b = ctx.p.data.brief;
    clear(summaryCard);
    const row = (k: string, v: string) => [h("dt", null, k), h("dd", { class: v ? null : "empty" }, v || "아직 안 정했어요")];
    summaryCard.append(
      h("h2", { class: "head-title" }, "요약"),
      h(
        "dl",
        { class: "summary-list" },
        row("행사 이름", b.eventName),
        row("종류", [byId(EVENT_KINDS, b.eventKind)?.ko, b.eventType].filter(Boolean).join(" · ")),
        row("장소", [(b.venueMode || "virtual") === "real" ? b.venueName || "실제 장소" : "가상 장소", byId(VENUE_TYPES, b.venueType)?.ko].filter(Boolean).join(" · ")),
        row("규모", b.capacity ? capacityKo(b.capacity) : ""),
        row("메모", b.notes),
      ),
    );
  }

  drawVenue();
  drawCap();
  drawSummary();
  body.append(
    h(
      "div",
      { class: "two-col" },
      h(
        "div",
        { class: "card" },
        // "고르는 대로 저장돼요"는 아래 막대에 이미 있다
        cardHead("행사 정보", { help: "고르는 대로 저장돼요. 장소와 규모는 3D 모형과 AI 장면 글에 쓰여요." }),
        h(
          "div",
          { class: "form-section stack" },
          h("h3", { class: "form-title" }, "행사"),
          text("행사 이름", "eventName", b0.eventName, "예: 2027 충청 유니버시아드 개회식"),
          h(
            "div",
            { class: "field" },
            h("span", { class: "field-label" }, "어떤 행사인가요"),
            chips(EVENT_KINDS, b0.eventKind ? [b0.eventKind] : [], false, (v) => void send({ eventKind: v[0] }).then(() => (syncExamples(), drawVenue())), [], "행사 종류"),
          ),
        ),
        h(
          "div",
          { class: "form-section stack" },
          h("h3", { class: "form-title" }, "장소"),
          chips(
            [
              { id: "real", ko: "실제 장소가 있어요", en: "" },
              { id: "virtual", ko: "가상의 장소예요", en: "" },
            ],
            [b0.venueMode || "virtual"],
            false,
            (v) => {
              venueNameField.hidden = v[0] !== "real";
              void send({ venueMode: v[0] });
            },
            [],
            "실제 장소인지",
          ),
          venueNameField,
          venueBox,
        ),
        h("div", { class: "form-section stack" }, h("h3", { class: "form-title" }, "규모"), capBox),
        // 고르지 않아도 되는 두 칸은 한 구획에 모은다. 휴대폰에서는 적은 것 한 줄로 접는다
        foldSm(
          h("div", { class: "stack" }, h("h3", { class: "form-title" }, "더 적기 ", opt), typeField, text("메모", "notes", b0.notes, "콘셉트, 꼭 보여야 하는 것, 발주처가 원하는 느낌 등", undefined, true)),
          { title: "더 적기 (선택)", summary: [b0.eventType, b0.notes].filter(Boolean).join(" · ") || "행사 종류 자세히 · 메모", class: "form-section" },
        ),
      ),
      h("aside", { class: "hide-sm" }, summaryCard),
    ),
  );
  ctx.bar({ status: saveState, primary: h("button", { class: "btn primary", type: "button", onclick: () => ctx.step("space") }, nextLabel("다음: 장소")) });
}

// ───────── ② 장소 ─────────

function renderSpace(body: HTMLElement, ctx: Ctx) {
  const visible = () => ctx.p.data.space.filter((x) => x.source !== "stage" && x.source !== "stage-angle");

  async function upload() {
    const files = await pickFiles(true);
    if (!files.length) return;
    const room = 6 - ctx.p.data.space.length;
    if (files.length > room) toast(`장소 그림은 6장까지예요. 앞의 ${room}장만 올릴게요.`, "info");
    for (const [i, f] of files.slice(0, room).entries()) {
      const label = ctx.p.data.space.length === 0 && i === 0 ? "관중석 위 전경" : `다른 방향 ${ctx.p.data.space.length + 1}`;
      toast(`${f.name} 올리는 중…`, "info", 2000);
      try {
        const { blob, w, h: hh } = await shrinkImage(f);
        ctx.set(await api<Project>(`/api/projects/${ctx.p.id}/space?label=${encodeURIComponent(label)}&w=${w}&h=${hh}`, { raw: blob, rawType: "image/jpeg" }));
        drawAll();
      } catch (err) {
        toast(`${f.name}: ${errMsg(err)}`, "err", 7000);
      }
    }
  }

  const open3d = () =>
    void openSpaceBuilder(
      ctx.p,
      (np) => {
        ctx.set(np);
        drawAll();
      },
      (goNext) => {
        if (goNext && ctx.p.data.model3d) ctx.step("stage");
        else drawAll();
      },
    );

  function methods(): HTMLElement {
    const m = ctx.p.data.model3d;
    const full = ctx.p.data.space.length >= 6;
    // 화면에 안 보이는 확정 무대 그림도 6장 한도에 들어간다. 막혔을 때 그 수를 밝힌다
    const hidden = ctx.p.data.space.length - visible().length;
    const fullNote = hidden ? `확정 무대 그림 ${hidden}장을 포함해 6장이 찼어요` : "6장까지예요 · 하나를 빼면 올릴 수 있어요";
    return h(
      "div",
      { class: "method-grid" },
      h(
        "button",
        { class: "method-card", type: "button", onclick: open3d },
        h("span", { class: "method-icon" }, icon("cube", 24)),
        h(
          "span",
          { class: "method-text" },
          h("span", { class: "method-title" }, h("b", null, m ? "3D 모형 열기" : "3D로 만들기"), h("span", { class: `chip ${m ? "ok" : "info"}` }, m ? "만들어 둠" : "추천")),
          h("span", { class: "d" }, m ? [VENUE_TYPES.find((v) => v.id === m.venueType)?.ko, capacityKo(m.capacity), stageKo(m.stage)].filter(Boolean).join(" · ") : "장소와 규모를 고르면 3D 모형이 생겨요"),
          h("span", { class: "xs muted" }, "가상 장소, 스케치업이 없을 때"),
        ),
      ),
      h(
        "div",
        { class: "method-wrap" },
        h(
          "button",
          { class: "method-card", type: "button", disabled: full, onclick: () => void upload() },
          h("span", { class: "method-icon" }, icon("upload", 24)),
          h(
            "span",
            { class: "method-text" },
            h("span", { class: "method-title" }, h("b", null, "그림 올리기")),
            h("span", { class: "d" }, "스케치업 화면·현장 사진·도면"),
            h("span", { class: `xs ${full ? "warn-text" : "muted"}` }, full ? fullNote : "JPG·PNG 여러 장"),
          ),
        ),
        h("span", { class: "method-help" }, helpTip("첫 그림(기준 그림)의 카메라 그대로 컷을 만들어요. 나머지 그림은 AI가 장소를 입체로 이해하는 데 써요.")),
      ),
    );
  }

  function nameEditor(s: { id: string; label: string }): HTMLElement {
    const slot = h("div", { class: "space-name" });
    const view = () => {
      clear(slot);
      slot.append(h("span", { class: "name-text", title: s.label }, s.label), h("button", { class: "btn ghost small icon-only", type: "button", "aria-label": `${s.label} 이름 바꾸기`, "data-k": `name-${s.id}`, onclick: edit }, icon("pencil", 16)));
    };
    const edit = () => {
      clear(slot);
      // 이름을 바꾼 뒤 다시 그려도 같은 그림의 연필 단추로 초점이 돌아온다 (같은 data-k)
      const input = h("input", { value: s.label, maxlength: 60, list: "space-labels", "aria-label": "어느 방향 그림인지", "data-k": `name-${s.id}` });
      let done = false;
      const finish = async (save: boolean) => {
        if (done) return;
        done = true;
        if (!save || input.value.trim() === s.label || !input.value.trim()) {
          const had = document.activeElement === input;
          view();
          if (had) slot.querySelector<HTMLElement>("button")?.focus();
          return;
        }
        try {
          ctx.set(await api<Project>(`/api/projects/${ctx.p.id}/space/${s.id}`, { method: "PATCH", body: { label: input.value } }));
          drawAll();
        } catch (err) {
          toast(errMsg(err), "err");
          view();
        }
      };
      input.addEventListener("keydown", (e) => {
        if (e.key === "Enter") {
          e.preventDefault();
          void finish(true);
        } else if (e.key === "Escape") {
          e.preventDefault();
          e.stopPropagation();
          void finish(false);
        }
      });
      input.addEventListener("blur", () => void finish(true));
      slot.append(input);
      input.focus();
      input.select();
    };
    view();
    return slot;
  }

  function gallery(): HTMLElement {
    const list = visible();
    const box = h("section", { class: "space-section" }, h("h2", { class: "section-title" }, "장소 그림", h("span", { class: "count num" }, String(list.length))));
    if (!list.length) {
      box.append(emptyState({ icon: "image", title: "아직 장소 그림이 없어요", body: "3D로 만들거나 그림을 올려 주세요" }));
      return box;
    }
    box.append(
      h(
        "div",
        { class: "space-grid", "data-gallery": "space" },
        list.map((s) =>
          h(
            "div",
            { class: `card space-card${s.base ? " base" : ""}` },
            h("div", { class: "thumb" }, h("img", { class: "zoomable", src: mediaUrl(s.key), alt: s.label, loading: "lazy" }), s.base ? h("span", { class: "thumb-badge", title: "컷의 카메라가 이 그림을 따라요" }, icon("star", 12), "기준") : null),
            h(
              "div",
              { class: "space-meta" },
              nameEditor(s),
              menu(
                [
                  ...(s.base
                    ? []
                    : [
                        {
                          label: "기준 그림으로 정하기",
                          icon: "star" as const,
                          onclick: async () => {
                            try {
                              ctx.set(await api<Project>(`/api/projects/${ctx.p.id}/space/${s.id}/base`, { body: {} }));
                              drawAll();
                              toast("기준 그림을 바꿨어요. 장소를 다시 읽어 주세요", "info");
                            } catch (err) {
                              toast(errMsg(err), "err");
                            }
                          },
                        },
                      ]),
                  {
                    label: "빼기",
                    icon: "trash",
                    danger: true,
                    onclick: async () => {
                      if (!(await confirmBox("그림 빼기", "이 장소 그림을 뺄까요?", "빼기", { danger: true }))) return;
                      try {
                        ctx.set(await api<Project>(`/api/projects/${ctx.p.id}/space/${s.id}`, { method: "DELETE" }));
                        drawAll();
                      } catch (err) {
                        toast(errMsg(err), "err");
                      }
                    },
                  },
                ],
                `${s.label} 메뉴`,
              ),
            ),
          ),
        ),
      ),
    );
    return box;
  }

  function drawBar() {
    const m = ctx.p.data.model3d;
    // 화면에 보이는 장소 그림만 센다 (확정 무대 그림은 ③에서 쓰는 것이라 여기서 판단에 넣지 않는다)
    const vis = visible();
    const n = vis.length;
    const toStage = (label = "다음: 무대 디자인") => h("button", { class: "btn primary", type: "button", onclick: () => ctx.step("stage") }, nextLabel(label));
    // 3D 공간에서 만든 그림만 있으면 요소를 이미 알고 있으니 AI 읽기 대신 다음 단계로
    if (m && !vis.some((s) => s.source !== "model3d")) {
      const views = vis.filter((s) => s.source === "model3d").length;
      ctx.bar({
        status: views ? `3D 모형 · 저장한 시점 ${views}개` : "3D 모형을 만들었어요 · 시점을 저장하면 컷의 카메라가 돼요",
        primary: toStage(),
      });
    } else if (n) {
      const read = ctx.p.data.analysis;
      const analyzeBtn = h("button", { class: read ? "btn" : "btn primary", type: "button" }, icon("sparkles"), read ? h("span", null, h("span", { class: "hide-sm" }, "장소 "), "다시 읽기") : "AI로 장소 읽기");
      analyzeBtn.addEventListener("click", () =>
        busy(
          analyzeBtn,
          "장소를 읽는 중",
          async () => {
            if (!ctx.p.data.space.length) throw new Error("장소 그림을 먼저 올려 주세요.");
            ctx.set(await api<Project>(`/api/projects/${ctx.p.id}/analyze`, { body: {} }));
            toast("다 읽었어요. 찾은 것을 확인해 주세요", "ok");
            ctx.step("elements");
          },
          { timer: true },
        ),
      );
      // 이미 읽었으면 다시 읽기는 보조로 내리고 다음 단계로 가는 단추를 주 단추로
      if (read) ctx.bar({ status: `장소 그림 ${n}장 · AI가 읽었어요`, secondary: [analyzeBtn], primary: toStage() });
      else ctx.bar({ status: `장소 그림 ${n}장 · AI로 읽으면 그림 속 요소를 찾아 줘요`, primary: analyzeBtn });
    } else {
      ctx.bar({
        status: "3D로 만들거나 그림을 올려 주세요",
        // 단계 순서(②→③)를 지킨다
        secondary: [h("button", { class: "btn ghost", type: "button", onclick: () => ctx.step("stage") }, "그림 없이 넘어가기")],
        primary: h("button", { class: "btn primary", type: "button", disabled: true }, nextLabel("다음")),
      });
    }
  }

  function drawAll() {
    redraw(body, () => {
      clear(body);
      body.append(methods(), h("datalist", { id: "space-labels" }, LABELS.map((l) => h("option", { value: l }))), gallery());
    });
    drawBar();
  }
  drawAll();
}

// ───────── ④ 요소 합의 ─────────

function kindSelect(value: string, hint: HTMLElement) {
  const sel = h(
    "select",
    { "aria-label": "종류" },
    ELEMENT_KINDS.map((k) => h("option", { value: k.id, selected: k.id === value }, k.ko)),
  );
  const sync = () => {
    const t = byId(ELEMENT_KINDS, sel.value)?.hint || "";
    hint.textContent = t;
    hint.hidden = !t;
  };
  sel.addEventListener("change", sync);
  sync();
  return sel;
}

function renderElements(body: HTMLElement, ctx: Ctx) {
  const admin = Boolean(state.me?.admin);
  const left = h("div", { class: "stack" });
  const right = h("aside");
  let showEn = false;
  let consOpen: boolean | null = null;
  const desktop = () => window.matchMedia("(min-width: 961px)").matches;

  const act = async (btn: HTMLButtonElement | null, label: string, path: string, payload: unknown = {}) =>
    busy(btn, label, async () => {
      ctx.set(await api<Project>(`/api/projects/${ctx.p.id}/elements/${path}`, { body: payload }));
      draw();
    });
  const actQuiet = async (path: string) => {
    try {
      ctx.set(await api<Project>(`/api/projects/${ctx.p.id}/elements/${path}`, { body: {} }));
      draw();
    } catch (err) {
      toast(errMsg(err), "err");
    }
  };

  function editor(e: Element | null, onDone: () => void): HTMLElement {
    const name = h("input", { value: e?.name || "", maxlength: 40, placeholder: "예: 원통형 LED" });
    const kindHint = h("span", { class: "help" });
    const kind = kindSelect(e?.kind || "other", kindHint);
    const what = h("textarea", { rows: 3, maxlength: 400, placeholder: "무엇인지 쉬운 말로. 예: 위아래로 오르내리는 원통형 LED이고, 둘레 전체에 영상이 나와요." }, e?.what || "") as HTMLTextAreaElement;
    const where = h("input", { value: e?.where || "", maxlength: 120, placeholder: "예: 중앙 원형 무대 바로 위" });
    const save = h("button", { class: "btn small", type: "submit" }, icon("check", 16), e ? "이 내용으로 확정" : "더하고 확정");
    return h(
      "form",
      {
        class: "stack tight editor",
        onsubmit: async (ev: Event) => {
          ev.preventDefault();
          await busy(
            save,
            "정리하는 중",
            async () => {
              const payload = { name: name.value, kind: kind.value, what: what.value, where: where.value };
              if (!payload.name.trim() || !payload.what.trim()) throw new Error("'이름'과 '무엇인가요' 칸을 채워 주세요.");
              ctx.set(await api<Project>(e ? `/api/projects/${ctx.p.id}/elements/${e.id}/confirm` : `/api/projects/${ctx.p.id}/elements`, { body: payload }));
              onDone();
              draw();
            },
            { timer: true },
          );
        },
      },
      h("div", { class: "row two" }, h("label", null, "이름", name), h("label", null, "종류", kind, kindHint)),
      h("label", null, "무엇인가요", what),
      h("label", null, "어디에", where),
      h("div", { class: "row end" }, h("button", { class: "btn ghost small", type: "button", onclick: onDone }, "취소"), save),
    );
  }

  const kindChip = (e: Element) => {
    const kind = byId(ELEMENT_KINDS, e.kind);
    return kind ? h("span", { class: "chip", title: kind.hint || null }, kind.ko) : null;
  };
  const enLine = (e: Element) => (admin ? devDetails("전문가용 · 그림 모델에 가는 문장", h("code", null, e.en, e.notEn ? ` (NOT ${e.notEn})` : "")) : null);

  function proposedCard(e: Element): HTMLElement {
    const slot = h("div");
    const openEditor = () => {
      clear(slot);
      slot.append(editor(e, () => clear(slot)));
    };
    return h(
      "div",
      { class: "card el-card" },
      h(
        "div",
        { class: "el-head" },
        h("h3", null, e.name),
        kindChip(e),
        h("span", { class: "spacer" }),
        menu(
          [
            {
              label: "잘못 찾았어요",
              icon: "trash",
              danger: true,
              onclick: async () => {
                try {
                  ctx.set(await api<Project>(`/api/projects/${ctx.p.id}/elements/${e.id}`, { method: "DELETE" }));
                  draw();
                } catch (err) {
                  toast(errMsg(err), "err");
                }
              },
            },
          ],
          `${e.name} 메뉴`,
        ),
      ),
      h("p", null, e.what),
      e.where ? h("p", { class: "small muted" }, `위치: ${e.where}`) : null,
      e.question ? h("p", { class: "notice warn small" }, icon("info", 16), h("span", null, e.question)) : null,
      enLine(e),
      h(
        "div",
        { class: "row wrap el-actions" },
        h("button", { class: "btn positive small", type: "button", onclick: (ev: Event) => act(ev.currentTarget as HTMLButtonElement, "", `${e.id}/confirm`) }, icon("check", 16), "맞아요"),
        h("button", { class: "btn small", type: "button", onclick: openEditor }, icon("pencil", 16), "고쳐서 확정"),
        h("button", { class: "btn ghost small", type: "button", onclick: (ev: Event) => act(ev.currentTarget as HTMLButtonElement, "", `${e.id}/exclude`) }, "이번엔 없어요"),
      ),
      slot,
    );
  }

  function rowCard(e: Element): HTMLElement {
    const slot = h("div");
    const excluded = e.status === "excluded";
    const mainBtn = h(
      "button",
      {
        type: "button",
        class: "el-row-main",
        "aria-expanded": "false",
        onclick: () => {
          const open = row.classList.toggle("open");
          mainBtn.setAttribute("aria-expanded", open ? "true" : "false");
        },
      },
      h("span", { class: `el-mark ${excluded ? "no" : "ok"}` }, icon(excluded ? "x" : "check", 14, excluded ? "이번엔 없음" : "확정")),
      h(
        "span",
        { class: "el-row-text" },
        h("span", { class: "el-row-title" }, h("b", null, e.name), kindChip(e)),
        h("span", { class: "el-row-what" }, e.what),
        e.where ? h("span", { class: "el-row-where" }, `위치: ${e.where}`) : null,
      ),
    );
    const row: HTMLElement = h(
      "div",
      { class: "el-row" },
      h(
        "div",
        { class: "el-row-top" },
        mainBtn,
        menu(
          [
            {
              label: "고치기",
              icon: "pencil",
              onclick: () => {
                clear(slot);
                slot.append(editor(e, () => clear(slot)));
              },
            },
            { label: "다시 검토", icon: "refresh", onclick: () => void actQuiet(`${e.id}/reset`) },
          ],
          `${e.name} 메뉴`,
        ),
      ),
      slot,
      admin ? h("div", { class: "el-row-dev" }, enLine(e)) : null,
    );
    return row;
  }

  function constraintsPanel(): HTMLElement {
    const d = ctx.p.data;
    const active = d.constraints.filter((c) => c.active);
    const gone = d.constraints.filter((c) => !c.active);
    const list = h(
      "ol",
      { class: "constraints" },
      active.map((c) =>
        h(
          "li",
          { class: "c-item" },
          h("span", { class: "c-num num" }, String(c.n)),
          h("div", { class: "c-text" }, h("span", null, c.ko), admin && showEn ? h("span", { class: "en xs" }, c.en) : null, h("span", { class: `chip from-${c.from}` }, c.from === "element" ? "요소" : c.from === "fix" ? "고치며" : "직접")),
          h(
            "button",
            {
              class: "btn ghost small icon-only c-x",
              type: "button",
              title: "이 조건 빼기",
              "aria-label": `${c.n}번 조건 빼기`,
              onclick: async () => {
                if (!(await confirmBox(`${c.n}번 조건 빼기`, `"${c.ko}"\n\n다음 그림부터 이 조건을 뺄까요? 기록은 남아요.`, "빼기", { danger: true }))) return;
                try {
                  ctx.set(await api<Project>(`/api/projects/${ctx.p.id}/constraints/${c.n}/retract`, { body: { note: "사용자가 뺌" } }));
                  draw();
                } catch (err) {
                  toast(errMsg(err), "err");
                }
              },
            },
            icon("x", 16),
          ),
        ),
      ),
    );
    const input = h("input", { placeholder: "예: 전광판에는 글자 없이 그래픽만", maxlength: 300, "aria-label": "조건 직접 적기" });
    const add = h("button", { class: "btn", type: "submit" }, "더하기");
    const enSwitch = h("input", { type: "checkbox", class: "switch", role: "switch", checked: showEn });
    enSwitch.addEventListener("change", () => {
      showEn = enSwitch.checked;
      draw();
    });
    // summary 는 단추 역할이라 제목과 도움말 단추를 그 안에 두지 않는다 (도움말은 본문 첫 줄로)
    const summary = h("summary", { class: "cons-summary" }, h("span", { class: "head-title cons-title" }, "지키는 조건", h("span", { class: "chip num" }, String(active.length))), icon("chevron-down", 18));
    // 넓은 화면(961px~)에서는 늘 펼쳐 둔다. 좁은 화면에서 접은 뒤 넓어져도 열린다
    const det = h(
      "details",
      { class: "card constraints-card", open: desktop() || (consOpen ?? false) },
      summary,
      h(
        "div",
        { class: "stack tight cons-body" },
        h(
          "p",
          { class: "small muted cons-note" },
          "모든 그림에 그대로 들어가요",
          helpTip("확정한 요소, 고치기에서 정한 사실, 직접 적은 조건이 번호대로 쌓여요. 쌓인 조건은 바꾸지 않아요. 필요 없으면 빼고 새로 더해 주세요."),
        ),
        gone.length ? h("p", { class: "xs muted" }, "뺀 조건 번호는 비워 둬요") : null,
        active.length ? list : h("p", { class: "small muted" }, "요소를 확정하면 여기에 쌓여요"),
        h(
          "form",
          {
            class: "row cons-add",
            onsubmit: async (e: Event) => {
              e.preventDefault();
              await busy(add, "", async () => {
                if (!input.value.trim()) throw new Error("조건을 적어 주세요.");
                ctx.set(await api<Project>(`/api/projects/${ctx.p.id}/constraints`, { body: { ko: input.value } }));
                input.value = "";
                draw();
              });
            },
          },
          input,
          add,
        ),
        admin ? h("label", { class: "toggle small" }, enSwitch, "영어 문장도 보기 (운영자)") : null,
        gone.length ? h("details", { class: "gone" }, h("summary", { class: "small muted" }, `뺀 조건 ${gone.length}개`), h("ul", { class: "small muted" }, gone.map((c) => h("li", null, `${c.n}. ${c.ko} — ${c.retractNote || "뺌"}`)))) : null,
      ),
    );
    summary.addEventListener("click", (e) => {
      if (desktop()) e.preventDefault();
      else consOpen = !det.open;
    });
    const mq = window.matchMedia("(min-width: 961px)");
    const onWide = () => {
      if (!det.isConnected) return mq.removeEventListener("change", onWide);
      if (mq.matches) det.open = true;
    };
    mq.addEventListener("change", onWide);
    return det;
  }

  function drawBar() {
    const d = ctx.p.data;
    const pending = d.elements.filter((e) => e.status === "proposed").length;
    const confirmed = d.elements.filter((e) => e.status === "confirmed").length;
    const nConds = d.constraints.filter((c) => c.active).length;
    const stageDone = Boolean(d.stage?.approvedKey || d.stage?.skipped);
    if (d.stage?.approvedKey && !d.analysis) {
      // 무대를 확정했으면 그 무대를 AI에게 읽혀 무대 요소를 정한다
      const read = h("button", { class: "btn primary", type: "button" }, icon("sparkles"), "AI로 확정 무대 읽기");
      read.addEventListener("click", () =>
        busy(
          read,
          "무대를 읽는 중",
          async () => {
            ctx.set(await api<Project>(`/api/projects/${ctx.p.id}/analyze`, { body: {} }));
            draw();
          },
          { timer: true },
        ),
      );
      ctx.bar({ status: "확정한 무대에서 구조·화면·재질을 찾아요", primary: read });
      return;
    }
    if (!stageDone) {
      // 무대를 아직 정하지 않았으면 앞 단계(③)로 돌아가게 한다. '다음'이라 부르지 않고, 같은 곳을 가리키는 뒤로 단추는 뺀다
      const status = pending ? `확인할 요소 ${pending}개 남음 · 무대 디자인을 정하면 장면으로 가요` : d.elements.length ? `요소 ${confirmed}개 확정 · 무대 디자인을 정하면 장면으로 가요` : "무대 디자인을 먼저 정해 주세요";
      ctx.bar({
        back: null,
        status,
        warn: pending > 0,
        primary: h("button", { class: "btn primary", type: "button", onclick: () => ctx.step("stage") }, icon("chevron-left", 16), h("span", null, "무대 디자인으로", h("span", { class: "hide-sm" }, " 돌아가기"))),
      });
      return;
    }
    const nextBtn = (disabled: boolean) => h("button", { class: "btn primary", type: "button", disabled, onclick: () => ctx.step("scene") }, nextLabel("다음: 장면"));
    if (pending) ctx.bar({ status: `확인할 요소 ${pending}개 남음`, warn: true, primary: nextBtn(true) });
    else if (!d.elements.length) ctx.bar({ status: "요소를 하나 이상 정해 주세요", warn: true, primary: nextBtn(true) });
    else ctx.bar({ status: `요소 ${confirmed}개 확정 · 조건 ${nConds}개`, primary: nextBtn(false) });
  }

  function draw() {
    clear(left);
    clear(right);
    const d = ctx.p.data;
    const pending = d.elements.filter((e) => e.status === "proposed");
    const confirmed = d.elements.filter((x) => x.status === "confirmed");
    const excluded = d.elements.filter((x) => x.status === "excluded");

    if (d.stage?.approvedKey && !d.analysis) {
      left.append(
        h(
          "div",
          { class: "card flat read-card" },
          h("img", { class: "zoomable", src: mediaUrl(d.stage.approvedKey), alt: "확정한 무대" }),
          h("div", null, cardHead("확정한 무대 읽기", { eyebrow: "p1", sub: "AI가 구조·화면·재질을 찾아 확인할 목록을 만들어요" })),
        ),
      );
    } else {
      left.append(h("div", { class: "el-intro" }, cardHead("요소 합의", { eyebrow: "p1", sub: "확인한 요소는 모든 그림에 그대로 들어가요" })));
    }
    if (d.analysis?.questions.length) {
      left.append(
        h(
          "div",
          { class: "card flat" },
          h("h3", null, "AI가 본 장소"),
          d.analysis.cameraKo ? h("p", { class: "small" }, h("b", null, "카메라 · "), d.analysis.cameraKo) : null,
          h("ul", { class: "questions small" }, d.analysis.questions.map((q) => h("li", null, q))),
        ),
      );
    }

    const addSlot = h("div");
    const addBtn = (label: string) =>
      h(
        "button",
        {
          class: "btn",
          type: "button",
          onclick: () => {
            clear(addSlot);
            addSlot.append(h("div", { class: "card" }, h("h3", null, "요소 직접 더하기"), editor(null, () => clear(addSlot))));
            addSlot.querySelector("input")?.focus();
          },
        },
        icon("plus"),
        label,
      );

    if (!d.elements.length) {
      left.append(emptyState({ icon: "list-check", title: "아직 요소가 없어요", body: "장소를 AI로 읽거나 직접 더해 주세요", action: addBtn("요소 직접 더하기") }), addSlot);
    } else {
      if (pending.length) left.append(h("h2", { class: "section-title" }, h("span", { class: "dot warn" }), "확인할 것", h("span", { class: "count num" }, String(pending.length))), h("div", { class: "stack" }, pending.map(proposedCard)));
      if (confirmed.length) left.append(h("h2", { class: "section-title" }, h("span", { class: "dot ok" }), "확정", h("span", { class: "count num" }, String(confirmed.length))), h("div", { class: "card el-list" }, confirmed.map(rowCard)));
      if (excluded.length) left.append(h("details", { class: "el-excluded" }, h("summary", { class: "section-title" }, h("span", { class: "dot" }), "이번엔 없음", h("span", { class: "count num" }, String(excluded.length)), icon("chevron-down", 18)), h("div", { class: "card el-list" }, excluded.map(rowCard))));
      left.append(h("div", { class: "row" }, addBtn("요소 직접 더하기")), addSlot);
    }
    right.append(constraintsPanel());
    drawBar();
  }

  body.append(h("div", { class: "two-col el-layout" }, left, right));
  draw();
}

// ───────── ⑤ 장면 ─────────

function renderScene(body: HTMLElement, ctx: Ctx) {
  const admin = Boolean(state.me?.admin);
  const d = ctx.p.data;
  const s = d.scene;
  const composedBox = h("div", { class: "card stack tight composed" });
  const barStatus = h("span");
  let flash: any;
  const summaryText = () => {
    const sc = ctx.p.data.scene;
    return sc.scene ? sceneLine(sc) : "장면을 골라 주세요";
  };
  const showSummary = () => (barStatus.textContent = summaryText());
  const save = async (patch: Record<string, unknown>) => {
    try {
      ctx.set(await api<Project>(`/api/projects/${ctx.p.id}/scene`, { method: "PATCH", body: patch }));
      drawComposed();
      clearTimeout(flash);
      barStatus.textContent = "저장됨";
      flash = setTimeout(showSummary, 1500);
    } catch (err) {
      toast(errMsg(err), "err");
    }
  };
  const saveText = debounce((k: string, v: string) => void save({ [k]: v }), 600);

  const sceneCards = h("div", { class: "option-grid compact" });
  const drawScenes = () =>
    redraw(sceneCards, () => {
    clear(sceneCards);
    for (const o of SCENES) {
      const on = ctx.p.data.scene.scene === o.id;
      sceneCards.append(
        h(
          "button",
          {
            type: "button",
            class: `option-card scene-card${on ? " on" : ""}`,
            "aria-pressed": on ? "true" : "false",
            "data-k": `scene-${o.id}`,
            onclick: async () => {
              await save({ scene: o.id, ...(o.id === "structure" ? { people: ["none"], effects: [], time: "day", purpose: "explain" } : {}) });
              if (o.id === "structure") {
                toast("구조 확인용은 사람·효과 없이 낮으로 맞췄어요.", "info");
                go(`#/p/${ctx.p.id}/scene`);
                return;
              }
              drawScenes();
            },
          },
          h("span", { class: "t" }, plainKo(o.ko)),
          o.hint ? h("span", { class: "d" }, o.hint) : null,
        ),
      );
    }
    });
  drawScenes();

  const screens = h("textarea", { rows: 2, maxlength: 1500, placeholder: "예: 원통 LED와 세로 화면에 푸른 은하수 영상" }, s.screens) as HTMLTextAreaElement;
  screens.addEventListener("input", () => saveText("screens", screens.value));
  const extra = h("textarea", { rows: 3, maxlength: 1500, placeholder: "예: 선수단이 링 무대 안쪽에 모여 하늘을 올려다본다. 차분하고 뭉클한 작별 분위기" }, s.extra) as HTMLTextAreaElement;
  extra.addEventListener("input", () => saveText("extra", extra.value));
  const continuity = h("input", { type: "checkbox", class: "switch", role: "switch", checked: s.continuity });
  continuity.addEventListener("change", () => void save({ continuity: continuity.checked }));

  function drawComposed() {
    clear(composedBox);
    const c = ctx.p.data.scene.composed;
    const stale = c && c.inputHash !== sceneHash(ctx.p.data);
    const btn = h("button", { class: "btn small", type: "button" }, icon("sparkles", 16), c ? "다시 쓰기" : "미리 보기");
    btn.addEventListener("click", () =>
      busy(
        btn,
        "쓰는 중",
        async () => {
          ctx.set(await api<Project>(`/api/projects/${ctx.p.id}/scene/compose`, { body: {} }));
          drawComposed();
        },
        { timer: true },
      ),
    );
    composedBox.append(
      cardHead("AI가 쓴 장면", { right: btn }),
      !c
        ? h("p", { class: "muted small" }, "뽑을 때 AI가 자동으로 써요")
        : h(
            "div",
            { class: "stack tight" },
            stale ? h("span", { class: "chip warn" }, icon("alert", 14), "설정이 바뀌었어요") : null,
            h("p", { class: "pre" }, c.ko),
            c.assumptions.length ? h("div", { class: "notice info small" }, h("b", null, "AI가 정한 것"), h("ul", null, c.assumptions.map((a) => h("li", null, a)))) : null,
            admin ? devDetails("전문가용 · 그림 모델에 가는 영어", h("code", null, c.en)) : null,
          ),
    );
  }
  drawComposed();

  const hasSpace = d.space.length > 0;
  const fieldRow = (name: Child, control: HTMLElement) => h("div", { class: "field-row" }, h("div", { class: "name" }, name), h("div", null, control));
  const multiNote = h("span", { class: "name-note" }, "여러 개");
  body.append(
    h(
      "div",
      { class: "two-col" },
      h(
        "div",
        { class: "stack" },
        h("div", { class: "card" }, cardHead("어떤 장면인가요", { eyebrow: "p2" }), sceneCards),
        h(
          "div",
          { class: "card" },
          cardHead("세부 설정"),
          h(
            "div",
            { class: "field-rows" },
            fieldRow("그림 느낌", chips(TONES, [s.tone || "photo"], false, (v) => void save({ tone: v[0] }), [], "그림 느낌")),
            fieldRow("목적", chips(PURPOSES, [s.purpose], false, (v) => void save({ purpose: v[0] }), [], "목적")),
            fieldRow("시간", chips(TIMES, [s.time], false, (v) => void save({ time: v[0] }), [], "시간")),
            fieldRow("카메라", chips(CAMERAS_UI, [hasSpace ? s.camera : s.camera === "same" ? "" : s.camera], false, (v) => void save({ camera: v[0] }), hasSpace ? [] : ["same"], "카메라")),
            fieldRow(["사람", multiNote], chips(PEOPLE, s.people, true, (v) => void save({ people: v }), [], "사람")),
            fieldRow(["효과", multiNote.cloneNode(true)], chips(EFFECTS, s.effects, true, (v) => void save({ effects: v }), [], "효과")),
          ),
        ),
        // 고르지 않아도 되는 두 카드는 휴대폰에서 요약 한 줄로 접는다
        foldSm(
          h(
            "div",
            { class: "card stack" },
            cardHead("화면과 덧붙일 말"),
            h("label", null, "LED에 나올 것", screens),
            h("label", null, "더 하고 싶은 말", extra),
            d.canonCutId ? h("div", { class: "row" }, h("label", { class: "toggle" }, continuity, "승인본 분위기 이어가기"), helpTip("승인본의 빛·색감·관객 밀도를 이어가요.")) : null,
          ),
          { title: "화면과 덧붙일 말", summary: [s.screens && `LED: ${s.screens}`, s.extra].filter(Boolean).join(" · ") || "LED에 나올 것 · 더 하고 싶은 말", card: true },
        ),
        foldSm(assetsPanel(ctx, "scene"), {
          title: "소스·에셋",
          icon: "layers",
          summary: (d.assets || []).length ? `붙임 ${(d.assets || []).filter((a) => a.useScene).length}/${MAX_USED} · 보관함 ${(d.assets || []).length}개` : "로고나 참고 그림을 올려 두세요",
          card: true,
        }),
      ),
      h("aside", { class: "stack" }, composedBox),
    ),
  );
  showSummary();
  ctx.bar({ status: barStatus, primary: h("button", { class: "btn primary", type: "button", onclick: () => ctx.step("cuts") }, nextLabel("다음: 연출컷")) });
}
